import React, { useCallback, useState } from 'react';
import ImportTagGroupAutocomplete from '../../../../components/muiLegacy/Autocomplete/ImportTagGroupAutocomplete';
import ProjectAutocomplete from '../../../../components/muiLegacy/Autocomplete/ProjectAutocomplete';
import VoxelDialog from '../../../../components/voxel/Dialogs/VoxelDialog';
import VoxelDialogActionCancel from '../../../../components/voxel/Dialogs/VoxelDialogActionCancel';
import VoxelDialogActionSubmit from '../../../../components/voxel/Dialogs/VoxelDialogActionSubmit';
import VoxelDialogActions from '../../../../components/voxel/Dialogs/VoxelDialogActions';
import VoxelDialogContent from '../../../../components/voxel/Dialogs/VoxelDialogContent';
import VoxelStepCounter from '../../../../components/voxel/VoxelStepCounter';
import { ProjectGq } from '../../../../graphql/projects/models/ProjectGq';
import { projectsApiService } from '../../../../graphql/projects/projectsApiService';

export interface CopyTagGroupsDialogProps {
  project: ProjectGq;
  reload: () => Promise<any>;
  openDialog: boolean;
  setOpenDialog: React.Dispatch<React.SetStateAction<boolean>>;
}

const CopyTagGroupsDialog: React.FC<CopyTagGroupsDialogProps> = ({
  project,
  reload,
  openDialog,
  setOpenDialog,
}) => {
  const [selectedProject, setSelectedProject] = useState<Partial<ProjectGq> | null>(null);
  const [selectedTagGroups, setSelectedTagGroups] = useState<number[]>([]);

  const closeDialog = () => {
    setOpenDialog(false);
    setSelectedProject(null);
    setSelectedTagGroups([]);
  };

  const tagGroupsDisabled = !selectedProject || !(selectedTagGroups.length > 0);

  const [loading, setLoading] = useState(false);

  const copyTagGroupsMutation = projectsApiService.mutations.tags.useCopyTagGroups();
  const handleCopyTagGroups = useCallback(() => {
    setLoading(true);
    return copyTagGroupsMutation({
      sourceProjectId: selectedProject?.id,
      targetProjectId: project.id,
      tagGroupIds: selectedTagGroups,
    })
      .then((result) => reload())
      .then(closeDialog)
      .finally(() => setLoading(false));
  }, [selectedTagGroups]);

  return (
    <VoxelDialog
      open={openDialog}
      onClose={closeDialog}
      title="Copy tag group(s) from project"
      minWClass="min-w-[700px]"
    >
      <VoxelDialogContent classMinWidth="mt-4 flex flex-col gap-8">
        <div className='flex flex-row items-center gap-8'>
          <span className='pl-2 pt-6'>
            <VoxelStepCounter title={'1'} active={true} />
          </span>
          <ProjectAutocomplete
            project={project}
            selectedProject={selectedProject}
            setSelectedProject={setSelectedProject}
            setSelectedTagGroups={setSelectedTagGroups}
            disabled={loading}
          />
        </div>
        <div className='flex flex-row items-center gap-8'>
          <span className='pl-2 pt-6'>
            <VoxelStepCounter title={'2'} active={!!selectedProject} />
          </span>
          <ImportTagGroupAutocomplete
            selectedProject={selectedProject}
            selectedTagGroups={selectedTagGroups}
            setSelectedTagGroups={setSelectedTagGroups}
            disabled={loading || !selectedProject}
          />
        </div>
      </VoxelDialogContent>
      <VoxelDialogActions>
        <VoxelDialogActionCancel onClick={closeDialog} />
        <VoxelDialogActionSubmit
          disabled={tagGroupsDisabled || loading}
          onClick={handleCopyTagGroups}
          title={'Copy'}
        />
      </VoxelDialogActions>
    </VoxelDialog>
  );
};

export default CopyTagGroupsDialog;
